import http from '@utils/http';

/**
 * GET /api/v1/chat – list all chats and groups
 * @param {{ page?: number, limit?: number }} params
 */
export const getChatList = (params = {}) =>
    http.get('/chat', { params }).then((res) => {
        const envelope = res.data ?? {};
        const data = envelope?.data ?? envelope;

        // New API shape: { data: Chat[], total, page, limit, totalPages, ... }
        const items = Array.isArray(envelope?.data)
            ? envelope.data
            : Array.isArray(data)
                ? data
                : (data?.data ?? []);

        const total = envelope?.total ?? data?.total ?? (Array.isArray(items) ? items.length : 0);
        const page = envelope?.page ?? data?.page ?? params?.page ?? 1;
        const limit = envelope?.limit ?? data?.limit ?? params?.limit ?? 50;
        const totalPages = envelope?.totalPages ?? data?.totalPages ?? 1;

        return {
            items: Array.isArray(items) ? items : [],
            total,
            page,
            limit,
            totalPages,
        };
    });

/**
 * GET /api/v1/chat/:chatId – get chat details and messages (paginated).
 * @param {string} chatId
 * @param {{ page?: number, limit?: number }} params – page/limit for messages (default page=1, limit=10)
 */
export const getChat = (chatId, params = {}) =>
    http.get(`/chat/${chatId}`, { params }).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/chat/:chatId/messages/assets — shared assets in this chat (paginated).
 * @param {string} chatId
 * @param {{ type?: 'media'|'docs'|'links', page?: number, limit?: number }} params — `type` filters: images/media, documents, link attachments.
 * @returns {Promise<{ items: unknown[], total: number, page: number, limit: number, totalPages: number }>}
 */
export const getChatMessageAssets = (chatId, params = {}) => {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    const type = params.type ?? 'media';
    return http
        .get(`/chat/${chatId}/messages/assets`, { params: { type, page, limit } })
        .then((res) => {
            const envelope = res.data ?? {};
            const data = envelope?.data ?? envelope;
            const rawItems = data?.items ?? data?.data ?? (Array.isArray(data) ? data : []);
            const items = Array.isArray(rawItems) ? rawItems : [];
            const total = typeof data?.total === 'number' ? data.total : envelope?.total ?? items.length;
            const pageFrom = typeof data?.page === 'number' ? data.page : typeof envelope?.page === 'number' ? envelope.page : page;
            const limitFrom = typeof data?.limit === 'number' ? data.limit : typeof envelope?.limit === 'number' ? envelope.limit : limit;
            const totalPages =
                typeof data?.totalPages === 'number'
                    ? data.totalPages
                    : typeof envelope?.totalPages === 'number'
                      ? envelope.totalPages
                      : total > 0 && limitFrom > 0
                        ? Math.max(1, Math.ceil(total / limitFrom))
                        : 1;
            return { items, total, page: pageFrom, limit: limitFrom, totalPages };
        });
};

/**
 * POST /api/v1/chat/:chatId/messages – send a text-only message (JSON).
 * @param {string} chatId
 * @param {string} text – HTML or plain text; stored as markdown
 * @param {{ parentMessageId?: string, attachments?: Array<{url, title?:}> }} options – parentMessageId for thread; attachments for link-only (same shape as discussion)
 */
export const sendMessage = (chatId, text, options = {}) => {
    const content = {
        type: 'markdown',
        text: text || '',
        mentions: [],
        hashtagRefs: [],
        ...(Array.isArray(options.attachments) && options.attachments.length > 0
            ? { attachments: options.attachments.map((a) => ({ url: a.url, title: a.title || a.url, filePath: '', mimeType: 'application/octet-stream', filename: a.title || 'Link', size: 0 })) }
            : {}),
    };
    return http
        .post(`/chat/${chatId}/messages`, {
            type: content.attachments ? 'media' : 'text',
            content,
            ...(options.parentMessageId ? { parentMessageId: options.parentMessageId } : {}),
        })
        .then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/chat/:chatId/messages – send message with files (multipart).
 * Do not set Content-Type; axios sets multipart/form-data with boundary.
 * @param {string} chatId
 * @param {{ content: { type: 'markdown', text: string, mentions: [], hashtagRefs: [] }, type: 'media'|'voice', parentMessageId?: string, attachmentUrls?: Array<{url,title?}>, files: File[] }} payload
 */
export const sendMessageMultipart = (chatId, payload) => {
    const { content, type = 'media', parentMessageId, attachmentUrls, files } = payload;
    const formData = new FormData();
    formData.append('content', JSON.stringify(content ?? { type: 'markdown', text: '', mentions: [], hashtagRefs: [] }));
    formData.append('type', type);
    if (parentMessageId) formData.append('parentMessageId', parentMessageId);
    if (Array.isArray(attachmentUrls) && attachmentUrls.length > 0) {
        formData.append('attachmentUrls', JSON.stringify(attachmentUrls));
    }
    (files ?? []).forEach((file) => formData.append('file', file));
    return http.post(`/chat/${chatId}/messages`, formData).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/chat/:chatId/messages/:msgId – edit message
 */
export const editMessage = (chatId, msgId, text) =>
    http
        .patch(`/chat/${chatId}/messages/${msgId}`, { content: { type: 'markdown', text } })
        .then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/chat/:chatId/messages/:msgId – delete (soft) message
 */
export const deleteMessage = (chatId, msgId) =>
    http.delete(`/chat/${chatId}/messages/${msgId}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/chat/:chatId/threads/:parentId – thread replies (paginated)
 * @param {{ page?: number, limit?: number }} [params]
 * @returns {Promise<{ items: unknown[], total: number, page: number, totalPages: number, limit: number }>}
 */
export const getThreadReplies = (chatId, parentId, params = {}) => {
    const limit = params.limit ?? 30;
    return http.get(`/chat/${chatId}/threads/${parentId}`, { params: { limit, ...params } }).then((res) => {
        const data = res.data?.data ?? res.data;
        const items = data?.items ?? (Array.isArray(data) ? data : []);
        const total = typeof data?.total === 'number' ? data.total : items.length;
        const page = typeof data?.page === 'number' ? data.page : params.page ?? 1;
        const limitFrom = typeof data?.limit === 'number' ? data.limit : limit;
        const totalPages =
            typeof data?.totalPages === 'number'
                ? data.totalPages
                : total > 0 && limitFrom > 0
                  ? Math.max(1, Math.ceil(total / limitFrom))
                  : 1;
        return { items, total, page, totalPages, limit: limitFrom };
    });
};

/**
 * POST /api/v1/chat/:chatId/reactions – add reaction
 * @param {string} chatId
 * @param {{ messageId: string, emoji: string }} body
 */
export const addReaction = (chatId, messageId, emoji) =>
    http.post(`/chat/${chatId}/reactions`, { messageId, emoji }).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/chat/:chatId/reactions – remove reaction (payload typically messageId + emoji or reactionId)
 */
export const removeReaction = (chatId, messageId, emoji) =>
    http.delete(`/chat/${chatId}/reactions`, { data: { messageId, emoji } }).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/chat/users/eligible – list users eligible for starting chats / creating groups.
 */
export const getEligibleChatUsers = (params = {}) =>
    http.get('/chat/users/eligible', { params }).then((res) => {
        const envelope = res.data ?? {};
        const data = envelope?.data ?? envelope;
        const users = Array.isArray(data?.users) ? data.users : [];
        const byRole = data?.byRole && typeof data.byRole === 'object' ? data.byRole : {};
        return { users, byRole };
    });

/**
 * POST /api/v1/chat – create DM or group chat
 * @param {{ type: 'dm'|'group', participantIds: string[], name?: string }} body
 */
export const createChat = (body) =>
    http.post('/chat', body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/chat/:chatId/read – mark chat as read for current user
 * @param {string} chatId
 */
export const markChatAsRead = (chatId) =>
    http.post(`/chat/${chatId}/read`).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/chat/:chatId/messages/:msgId/forward – forward message(s) to chats/users
 * Backend validator currently rejects messageIds in body.
 * Expected payload:
 *  { targetChatIds?: string[], targetUserIds?: string[] }
 */
export const forwardMessages = (chatId, msgId, body) =>
    http.post(`/chat/${chatId}/messages/${msgId}/forward`, body).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/chat/:chatId/messages/:msgId/thread-title – update thread title for a parent message
 * Payload: { threadTitle: string }
 */
export const updateThreadTitle = (chatId, msgId, threadTitle) =>
    http
        .patch(`/chat/${chatId}/messages/${msgId}/thread-title`, { threadTitle })
        .then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/chat/:chatId/clear – clear conversation (delete all messages for current user scope)
 */
export const clearChatConversation = (chatId) =>
    http.post(`/chat/${chatId}/clear`).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/chat/:chatId/group-name – rename group
 * Body: { name }
 */
export const renameChat = (chatId, name) =>
    http.patch(`/chat/${chatId}/group-name`, { name }).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/chat/:chatId/members – add members to a group chat
 * Body: { memberIds: string[] }
 */
export const addChatMembers = (chatId, memberIds = []) =>
    http.post(`/chat/${chatId}/members`, { memberIds }).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/chat/:chatId/members/:userId – remove member from a group chat
 */
export const removeChatMember = (chatId, userId) =>
    http.delete(`/chat/${chatId}/members/${userId}`).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/chat/:chatId/exit – exit group chat
 */
export const leaveChat = (chatId) =>
    http.post(`/chat/${chatId}/exit`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/chat/messages/search – search messages in a chat
 * Params: { chatId, term, page, limit }
 */
export const searchChatMessages = ({ chatId, term, page = 1, limit = 20 } = {}) =>
    http
        .get('/chat/messages/search', { params: { chatId, term, page, limit } })
        .then((res) => {
            const envelope = res.data ?? {};
            const data = envelope?.data ?? envelope;
            const items = data?.items ?? data?.data ?? (Array.isArray(data) ? data : []);
            const total = data?.total ?? envelope?.total ?? (Array.isArray(items) ? items.length : 0);
            const totalPages = data?.totalPages ?? envelope?.totalPages ?? 1;
            return { items: Array.isArray(items) ? items : [], total, page: data?.page ?? page, limit: data?.limit ?? limit, totalPages };
        });
